import { arrayOrEmpty, cleanString, numberOrNull, booleanValue } from './helpers';

/*
 * The Pricing step's rule editor uses a few extra rule "types"
 * (weekday, date_range, occupancy) and its own field names
 * (priceAdjustmentType/priceAdjustment) that don't exist on the
 * shared backend pricingRuleSchema. Map them onto the closest
 * supported backend values rather than sending an invalid enum
 * / missing required field.
 */
const SEASONAL_RULE_TYPES = ['weekend', 'season', 'holiday', 'festival', 'special_event', 'last_minute', 'early_bird'];

const SEASONAL_TYPE_MAP = {
    weekend: 'weekend',
    weekday: 'weekend',
    season: 'season',
    date_range: 'season',
    occupancy: 'special_event',
    holiday: 'holiday',
    festival: 'festival',
    special_event: 'special_event',
    last_minute: 'last_minute',
    early_bird: 'early_bird'
};

/*
 * Reverse of SEASONAL_TYPE_MAP. Both "weekend" and "weekday"
 * collapse onto the backend's "weekend" value, and both
 * "season" and "date_range" collapse onto "season" - so this
 * can only recover ONE of each pair. Rules created as "weekday"
 * or "date_range" will come back as "weekend"/"season" on
 * edit. That's an accepted, pre-existing lossy spot (the
 * backend schema simply doesn't have separate values for them),
 * not something introduced by this mapper.
 */
const BACKEND_TYPE_TO_FRONTEND = {
    weekend: 'weekend',
    season: 'season',
    holiday: 'holiday',
    festival: 'festival',
    special_event: 'occupancy',
    last_minute: 'last_minute',
    early_bird: 'early_bird'
};

/* ============================================================
   SEASONAL / DYNAMIC PRICING - FRONTEND -> BACKEND
============================================================ */

export function transformSeasonalPricing(rules = []) {
    return arrayOrEmpty(rules)
        .map((rule) => {
            const type = SEASONAL_TYPE_MAP[rule?.type] || (SEASONAL_RULE_TYPES.includes(rule?.type) ? rule.type : 'season');

            const adjustmentType = rule?.adjustmentType || rule?.priceAdjustmentType || 'fixed';

            // the adjustment; "Override price" is used only when no adjustment is typed
            const adjustmentValue =
                numberOrNull(rule?.adjustmentValue) ??
                (numberOrNull(rule?.priceAdjustment) || numberOrNull(rule?.price) || 0);

            return {
                _id: rule?._id || undefined,

                name: cleanString(rule?.name) || 'Pricing rule',

                type,

                startDate: rule?.startDate || null,

                endDate: rule?.endDate || null,

                adjustmentType: adjustmentType === 'percentage' ? 'percentage' : 'fixed',

                adjustmentValue,

                /*
                 * NOTE: the rule editor's "Minimum/Maximum
                 * Guests" fields (rule.minGuests/maxGuests) map
                 * to a GUEST count, but the only matching
                 * backend fields (minimumNights/maximumNights)
                 * are a NIGHT count - a different unit, not just
                 * a different name. Mapping guests into a
                 * "nights" field would silently store wrong
                 * data, so minGuests/maxGuests are intentionally
                 * left unsent here; the backend pricingRuleSchema
                 * needs its own guest-count fields before an
                 * Occupancy-type rule's guest range can be
                 * persisted at all.
                 */
                minimumNights: Math.max(1, numberOrNull(rule?.minimumNights) || 1),

                maximumNights: numberOrNull(rule?.maximumNights) || undefined,

                active: booleanValue(rule?.active, true)
            };
        })
        .filter((rule) => rule.name && rule.startDate && rule.endDate);
}

/* ============================================================
   SEASONAL / DYNAMIC PRICING - BACKEND -> FRONTEND
============================================================ */

export function reverseSeasonalPricing(rules = []) {
    return arrayOrEmpty(rules).map((rule) => ({
        _id: rule?._id,
        name: cleanString(rule?.name),
        type: BACKEND_TYPE_TO_FRONTEND[rule?.type] || 'season',
        startDate: rule?.startDate ? String(rule.startDate).slice(0, 10) : '',
        endDate: rule?.endDate ? String(rule.endDate).slice(0, 10) : '',
        /*
         * The backend never stores which days of the week a
         * weekend/weekday rule applies to (the rule editor
         * documents this as "a display hint only for now"), so
         * this always comes back empty on edit - not a
         * regression, just an already-documented gap.
         */
        days: [],
        // only the adjustment is stored (no separate override price)
        price: 0,
        priceAdjustmentType: rule?.adjustmentType === 'percentage' ? 'percentage' : 'fixed',
        priceAdjustment: numberOrNull(rule?.adjustmentValue) || 0,
        minimumNights: numberOrNull(rule?.minimumNights) || 1,
        maximumNights: numberOrNull(rule?.maximumNights),
        minGuests: 1,
        maxGuests: 0,
        active: booleanValue(rule?.active, true)
    }));
}
